import React, { use, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericTable from "../../../Components/GenericTable";
import { Button, Popconfirm, Tag, message } from "antd";
import refreshIcon from "../../../Assets/Icons/refresh.svg";
import copyIcon from "../../../Assets/Icons/copy.svg";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import "./style.css";
import {
  deleteApiKey,
  getAllApiKeys,
  getStatsAPI,
  renewApiKey,
} from "../../../API/apiKeys";

const APIKeys = () => {
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyStats, setApiKeyStats] = useState({});
  const navigate = useNavigate();

  const fetchAllKeys = async () => {
    try {
      setLoading(true);
      const response = await getAllApiKeys();
      if (Math.floor(response?.status / 100) === 2) {
        const data = response?.data || [];
        setApiKeys(data);
      } else {
        message.error("Unexpected response from server.");
      }
    } catch (error) {
      message.error("Failed to fetch API Keys");
    } finally {
      setLoading(false);
    }
  };

  const fetchAPIStats = async () => {
    try {
      setLoading(true);
      const response = await getStatsAPI();
      if (Math.floor(response?.status / 100) === 2) {
        const data = response?.data || [];
        setApiKeyStats(data);
      } else {
        message.error("Unexpected response from server.");
      }
    } catch (error) {
      message.error("Failed to fetch API Keys Stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllKeys();
    fetchAPIStats();
  }, []);

  const copyMaskedKey = (key) => {
    navigator.clipboard.writeText(key).then(() => {
      message.success("Key copied");
    });
  };

  const handleAddApi = () => {
    navigate("/create-api-key");
  };

  const refreshAPIKey = async (id) => {
    try {
      const response = await renewApiKey(id);
      if (Math.floor(response?.status / 100) === 2) {
        message.success("API Key renewed successfully");
        const updatedKey = response.data?.data?.key;

        console.log(response);
        console.log(updatedKey);
        navigate("/api-key-generated", { state: { apiKey: updatedKey } });
      } else {
        message.error("Failed to renew API key");
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteApiKey(id);
      if (Math.floor(response?.status / 100) === 2) {
        message.success("API Key deleted successfully");
        setApiKeys((prev) => prev.filter((item) => item.id !== id));
      } else {
        message.error("Failed to delete API key");
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: "name-column",
      enableSearch: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },

    {
      title: "Scope",
      dataIndex: "server_access",
      key: "server_access",
      filters: [
        { text: "all", value: "all" },
        { text: "selected", value: "selected" },
      ],
      onFilter: (value, record) => record.server_access === value,
    },

    {
      title: "Key",
      dataIndex: "key",
      key: "key",
      render: (text) => {
        const maskedKey = text ? text.slice(0, 4) + "••••••••••••" : "";

        return (
          <div className="masked-key">
            <span>{maskedKey}</span>
            <img
              src={copyIcon}
              alt="Copy key"
              className="copy-icon"
              onClick={() => copyMaskedKey(text)}
            />
          </div>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => new Date(value).toLocaleString(),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },

    {
      title: "Last Used",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value) => new Date(value).toLocaleString(),
      sorter: (a, b) => new Date(a.updated_at) - new Date(b.updated_at),
    },
    {
      title: "Permissions",
      key: "permissions",
      render: (_, record) => {
        const permissions = [];

        if (record.can_read) {
          permissions.push(
            <Tag color="blue" key="read">
              Read
            </Tag>
          );
        }

        if (record.can_send) {
          permissions.push(
            <Tag color="green" key="send">
              Send
            </Tag>
          );
        }

        if (record.can_manage) {
          permissions.push(
            <Tag color="purple" key="manage">
              Manage
            </Tag>
          );
        }

        return <>{permissions}</>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div className="action-icons">
          <img
            src={refreshIcon}
            alt="Refresh API Key"
            className="action-icon refresh"
            onClick={() => refreshAPIKey(record?.id)}
          />

          <Popconfirm
            title="Delete API Key"
            description="Are you sure you want to delete this API key?"
            onConfirm={() => handleDelete(record?.id)}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined
              style={{
                fontSize: 18,
                color: "#ff4d4f",
                cursor: "pointer",
                marginLeft: 12,
              }}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="api">
      <div className="stats-wrapper">
        <div className="stat-card">
          <div className="stat-title">Total Keys</div>
          <div className="stat-value">
            {apiKeyStats?.total_keys}
            <span
              className="stat-green"
              style={{
                color: apiKeyStats?.keys_this_month > 0 ? "green" : "red",
              }}
            >
              {apiKeyStats?.keys_this_month > 0
                ? `+${apiKeyStats?.keys_this_month}`
                : apiKeyStats?.keys_this_month}{" "}
              this month
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-title">Active Requests</div>
          <div className="stat-value">
            {apiKeyStats?.active_requests_24h}
            <span className="stat-muted">Last 24h</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-title">Usage Limit</div>
          <div className="progress-wrapper">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${apiKeyStats?.usage?.percent}` }}
              ></div>
            </div>
            <div className="stat-muted">
              {apiKeyStats?.usage?.percent}% of monthly quota used
            </div>
          </div>
        </div>
      </div>
      <div className="btn-container">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddApi}>
          Create Api Key
        </Button>
      </div>

      <GenericTable dataSource={apiKeys} columns={columns} />
    </div>
  );
};

export default APIKeys;
